import { useState, useEffect, useRef } from 'react';
import { generateDIYGuide, challengeStep } from '../../services/diyGuideAI';
import {
  getSavedGuide,
  saveGuide,
  updateGuideProgress,
  addChallengeToGuide,
  applyChallengeCorrection,
  deleteGuide,
} from '../../utils/storage';

const LOADING_STAGES = [
  'Analyzing service requirements...',
  'Looking up vehicle-specific procedures...',
  'Generating personalized guide...',
  'Finalizing your guide...',
];

export default function DIYGuide({ vehicle, item, onBack, onMarkComplete, onAskPocketMechanic }) {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState(LOADING_STAGES[0]);
  const [stageIdx, setStageIdx] = useState(0);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [completedSteps, setCompletedSteps] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [resumedFromSaved, setResumedFromSaved] = useState(false);
  const abortRef = useRef(null);

  // Challenge state
  const [challengingStep, setChallengingStep] = useState(null);
  const [challengeConcern, setChallengeConcern] = useState('');
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [challengeResult, setChallengeResult] = useState(null);
  const [showChallengeHistory, setShowChallengeHistory] = useState(false);
  const challengeAbortRef = useRef(null);

  useEffect(() => {
    // Check for saved guide first
    const saved = getSavedGuide(vehicle.id, item.service);
    if (saved && saved.guide) {
      setGuide(saved.guide);
      setCompletedSteps(saved.completedSteps || []);
      setChallenges(saved.challenges || []);
      setResumedFromSaved(true);
      setLoading(false);
      return;
    }

    // No saved guide — generate fresh
    const controller = new AbortController();
    abortRef.current = controller;

    const interval = setInterval(() => {
      setStageIdx((prev) => {
        const next = prev < LOADING_STAGES.length - 1 ? prev + 1 : prev;
        setStage(LOADING_STAGES[next]);
        return next;
      });
    }, 3000);

    async function fetchGuide() {
      try {
        const result = await generateDIYGuide(
          vehicle,
          item,
          controller.signal,
          (s) => setStage(s),
        );
        setGuide(result);
        // Save immediately after generation
        const savedRecord = saveGuide(vehicle.id, item.service, result);
        setChallenges(savedRecord.challenges || []);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[DIYGuide] Failed:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchGuide();

    return () => {
      clearInterval(interval);
      controller.abort();
    };
  }, [vehicle, item]);

  // Persist step progress whenever it changes
  useEffect(() => {
    if (guide && completedSteps.length >= 0) {
      updateGuideProgress(vehicle.id, item.service, completedSteps);
    }
  }, [completedSteps, guide, vehicle.id, item.service]);

  function toggleStep(num) {
    setExpandedSteps((prev) => ({ ...prev, [num]: !prev[num] }));
  }

  function expandAll() {
    if (!guide) return;
    const allExpanded = {};
    guide.steps.forEach((s) => { allExpanded[s.number] = true; });
    setExpandedSteps(allExpanded);
  }

  function collapseAll() {
    setExpandedSteps({});
  }

  function toggleStepComplete(num) {
    setCompletedSteps((prev) =>
      prev.includes(num) ? prev.filter((n) => n !== num) : [...prev, num],
    );
  }

  // Challenge flow
  function openChallenge(step) {
    setChallengingStep(step);
    setChallengeConcern('');
    setChallengeResult(null);
  }

  function cancelChallenge() {
    if (challengeAbortRef.current) challengeAbortRef.current.abort();
    setChallengingStep(null);
    setChallengeConcern('');
    setChallengeResult(null);
    setChallengeLoading(false);
  }

  async function submitChallenge() {
    if (!challengeConcern.trim() || !challengingStep) return;
    setChallengeLoading(true);
    setChallengeResult(null);

    const controller = new AbortController();
    challengeAbortRef.current = controller;

    try {
      const result = await challengeStep(
        vehicle,
        item.service,
        challengingStep,
        challengeConcern.trim(),
        controller.signal,
      );
      setChallengeResult(result);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[Challenge] Failed:', err);
      setChallengeResult({
        verdict: 'confirmed',
        explanation: 'Unable to verify at this time. Please try again later.',
        corrected_step: { ...challengingStep },
      });
    } finally {
      setChallengeLoading(false);
    }
  }

  function applyCorrection() {
    if (!challengeResult || !challengingStep) return;
    const correctedStep = challengeResult.corrected_step;

    // Update guide in state
    const updatedGuide = { ...guide };
    const stepIdx = updatedGuide.steps.findIndex((s) => s.number === challengingStep.number);
    if (stepIdx !== -1) {
      updatedGuide.steps[stepIdx] = {
        ...correctedStep,
        number: challengingStep.number,
        userCorrected: true,
        correctedAt: new Date().toISOString(),
      };
    }
    setGuide(updatedGuide);

    // Persist correction
    applyChallengeCorrection(vehicle.id, item.service, challengingStep.number, correctedStep);

    // Log challenge
    const challengeEntry = {
      stepNumber: challengingStep.number,
      stepTitle: challengingStep.title,
      originalDescription: challengingStep.description,
      concern: challengeConcern,
      verdict: challengeResult.verdict,
      explanation: challengeResult.explanation,
      correctedDescription: correctedStep.description,
      applied: true,
    };
    const updated = addChallengeToGuide(vehicle.id, item.service, challengeEntry);
    setChallenges(updated?.challenges || []);

    cancelChallenge();
  }

  function keepOriginal() {
    // Log challenge but don't apply
    const challengeEntry = {
      stepNumber: challengingStep.number,
      stepTitle: challengingStep.title,
      originalDescription: challengingStep.description,
      concern: challengeConcern,
      verdict: challengeResult?.verdict || 'confirmed',
      explanation: challengeResult?.explanation || '',
      correctedDescription: challengeResult?.corrected_step?.description || '',
      applied: false,
    };
    const updated = addChallengeToGuide(vehicle.id, item.service, challengeEntry);
    setChallenges(updated?.challenges || []);

    cancelChallenge();
  }

  // Count challenges in this session for abuse protection
  const sessionChallengeCount = challenges.length;
  const showAbuseWarning = sessionChallengeCount >= 3;

  const label = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;

  /* ─── Loading State ─── */
  if (loading) {
    return (
      <div className="screen" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={spinnerContainerStyle}>
            <div style={spinnerStyle} />
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute' }}>
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>
            Generating Your Guide
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 24 }}>
            {item.service} — {label}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left', maxWidth: 280, margin: '0 auto' }}>
            {LOADING_STAGES.map((s, i) => (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: i <= stageIdx ? 1 : 0.3, transition: 'opacity 0.4s' }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  background: i < stageIdx ? 'var(--success)' : i === stageIdx ? 'var(--accent)' : 'var(--bg-elevated)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {i < stageIdx && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                  )}
                  {i === stageIdx && (
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white', animation: 'pulse 1s infinite' }} />
                  )}
                </div>
                <span style={{ fontSize: '0.8125rem', color: i <= stageIdx ? 'var(--text-primary)' : 'var(--text-muted)' }}>{s}</span>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        `}</style>
      </div>
    );
  }

  /* ─── Error State ─── */
  if (!guide) {
    return (
      <div className="screen" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', marginBottom: 16 }}>
          Failed to generate guide. Please try again.
        </p>
        <button onClick={onBack} className="btn btn-secondary">Go Back</button>
      </div>
    );
  }

  /* ─── Step progress ─── */
  const totalSteps = guide.steps?.length || 0;
  const completedCount = completedSteps.length;
  const progressPct = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  // Get challenges for a specific step number
  function getChallengesForStep(stepNum) {
    return challenges.filter((c) => c.stepNumber === stepNum);
  }

  // Check if step was previously challenged
  function stepPreviouslyChallenged(stepNum) {
    return challenges.some((c) => c.stepNumber === stepNum);
  }

  return (
    <div className="screen" style={{ padding: 0, overflow: 'hidden', height: '100dvh', minHeight: 0 }}>
      {/* Scrollable content area */}
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '24px 20px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onBack} style={backBtnStyle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div style={{ flex: 1 }}>
          <h1 className="screen-title" style={{ fontSize: '1.15rem' }}>DIY Guide</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{label}</p>
        </div>
        {resumedFromSaved && (
          <span style={{
            padding: '3px 8px',
            borderRadius: 100,
            background: 'rgba(59, 130, 246, 0.12)',
            color: '#3b82f6',
            fontSize: '0.625rem',
            fontWeight: 600,
          }}>
            Saved
          </span>
        )}
      </div>

      {/* Progress bar */}
      {totalSteps > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              Progress: {completedCount}/{totalSteps} steps
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: progressPct === 100 ? 'var(--success)' : 'var(--accent)' }}>
              {progressPct}%
            </span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-elevated)', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${progressPct}%`,
              borderRadius: 3,
              background: progressPct === 100 ? 'var(--success)' : 'var(--accent)',
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
      )}

      {/* Service info card */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: '1.125rem', marginBottom: 8 }}>{item.service}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
          <InfoChip label="Difficulty" value={item.difficulty || 'Moderate'} />
          <InfoChip label="Time" value={guide.estimated_time} />
        </div>
        {guide.savings_vs_dealer && (
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.25)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span style={{ fontSize: '1.25rem' }}>💰</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--success)' }}>
                You Could Save: {guide.savings_vs_dealer}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>vs dealer pricing</div>
            </div>
          </div>
        )}
      </div>

      {/* What You'll Need */}
      <Section title="What You'll Need">
        {guide.tools?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={subheadingStyle}>Tools</div>
            {guide.tools.map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: t.essential ? 'var(--accent)' : 'var(--text-muted)', flexShrink: 0 }} />
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}>{t.name}</span>
                {!t.essential && <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>(optional)</span>}
              </div>
            ))}
          </div>
        )}
        {guide.parts?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={subheadingStyle}>Parts</div>
            {guide.parts.map((p, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 0', gap: 8 }}>
                <div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}>{p.name}</div>
                  {p.part_number && p.part_number !== 'OEM varies' && (
                    <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{p.part_number}</div>
                  )}
                </div>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--accent)', flexShrink: 0 }}>{p.estimated_cost}</span>
              </div>
            ))}
          </div>
        )}
        {guide.consumables?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={subheadingStyle}>Consumables</div>
            {guide.consumables.map((c, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}>{c.name}</span>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', flexShrink: 0 }}>{c.estimated_cost}</span>
              </div>
            ))}
          </div>
        )}
        {guide.optional_items?.length > 0 && (
          <div>
            <div style={subheadingStyle}>Optional / Nice-to-Have</div>
            {guide.optional_items.map((o, i) => (
              <div key={i} style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '3px 0' }}>
                • {o.name}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Before You Start */}
      <Section title="Before You Start">
        {guide.safety_warnings?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ ...subheadingStyle, color: 'var(--danger)' }}>⚠️ Safety Warnings</div>
            {guide.safety_warnings.map((w, i) => (
              <div key={i} style={{
                fontSize: '0.8125rem',
                color: 'var(--text-primary)',
                padding: '6px 0 6px 16px',
                borderLeft: '2px solid var(--danger)',
                marginBottom: 4,
                lineHeight: 1.4,
              }}>
                {w}
              </div>
            ))}
          </div>
        )}
        {guide.preparation?.length > 0 && (
          <div>
            <div style={subheadingStyle}>Preparation</div>
            {guide.preparation.map((p, i) => (
              <div key={i} style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '4px 0', lineHeight: 1.4 }}>
                {i + 1}. {p}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Vehicle-Specific Notes */}
      {guide.vehicle_specific_notes?.length > 0 && (
        <div className="card" style={{
          marginBottom: 16,
          padding: '14px 16px',
          background: 'rgba(59, 130, 246, 0.06)',
          border: '1px solid rgba(59, 130, 246, 0.18)',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 10,
            fontWeight: 700,
            fontSize: '0.8125rem',
            color: '#3b82f6',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            Know Before You Wrench
          </div>
          {guide.vehicle_specific_notes.map((note, i) => (
            <div key={i} style={{
              fontSize: '0.8125rem',
              color: 'var(--text-secondary)',
              lineHeight: 1.5,
              padding: '4px 0 4px 24px',
              position: 'relative',
            }}>
              <span style={{ position: 'absolute', left: 8, color: '#3b82f6' }}>•</span>
              {note}
            </div>
          ))}
        </div>
      )}

      {/* Step-by-Step Instructions */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h2 style={sectionTitleStyle}>Step-by-Step Instructions</h2>
          <button
            onClick={Object.keys(expandedSteps).length > 0 ? collapseAll : expandAll}
            style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}
          >
            {Object.keys(expandedSteps).length > 0 ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {guide.steps?.map((step) => {
            const isCompleted = completedSteps.includes(step.number);
            const isCorrected = step.userCorrected;
            const stepChallenges = getChallengesForStep(step.number);

            return (
              <div
                key={step.number}
                className="card"
                style={{
                  padding: 0,
                  overflow: 'hidden',
                  borderColor: isCorrected ? 'rgba(59, 130, 246, 0.3)' : undefined,
                  background: isCorrected ? 'rgba(59, 130, 246, 0.03)' : undefined,
                }}
              >
                <button
                  onClick={() => toggleStep(step.number)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    textAlign: 'left',
                  }}
                >
                  {/* Step number circle / completed check */}
                  <div
                    onClick={(e) => { e.stopPropagation(); toggleStepComplete(step.number); }}
                    style={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                      background: isCompleted ? 'var(--success)' : 'var(--accent)',
                      color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.75rem', fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                    }}
                    title={isCompleted ? 'Mark incomplete' : 'Mark complete'}
                  >
                    {isCompleted ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : step.number}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textDecoration: isCompleted ? 'line-through' : 'none',
                        opacity: isCompleted ? 0.6 : 1,
                      }}>
                        {step.title}
                      </span>
                      {isCorrected && (
                        <span style={{
                          padding: '1px 6px',
                          borderRadius: 100,
                          background: 'rgba(59, 130, 246, 0.12)',
                          color: '#3b82f6',
                          fontSize: '0.5625rem',
                          fontWeight: 600,
                        }}>
                          Verified by user
                        </span>
                      )}
                    </div>
                    {!expandedSteps[step.number] && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.3 }}>
                        {step.description.slice(0, 80)}{step.description.length > 80 ? '...' : ''}
                      </p>
                    )}
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ flexShrink: 0, marginTop: 4, transition: 'transform 0.2s', transform: expandedSteps[step.number] ? 'rotate(180deg)' : 'rotate(0)' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {expandedSteps[step.number] && (
                  <div style={{ padding: '0 16px 14px 56px', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                    <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 8 }}>
                      {step.description}
                    </p>
                    {step.details && (
                      <p style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', lineHeight: 1.5, marginBottom: 8, fontStyle: 'italic' }}>
                        {step.details}
                      </p>
                    )}
                    {step.pro_tip && (
                      <div style={{
                        fontSize: '0.8125rem',
                        color: 'var(--text-secondary)',
                        padding: '8px 12px',
                        background: 'rgba(34, 197, 94, 0.08)',
                        border: '1px solid rgba(34, 197, 94, 0.18)',
                        borderRadius: 'var(--radius-sm)',
                        lineHeight: 1.4,
                        marginBottom: 10,
                        display: 'flex',
                        gap: 8,
                        alignItems: 'flex-start',
                      }}>
                        <span style={{ flexShrink: 0, fontSize: '0.875rem' }}>🔧</span>
                        <div>
                          <span style={{ fontWeight: 600, color: 'var(--success)', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Pro Tip</span>
                          <div style={{ marginTop: 2 }}>{step.pro_tip}</div>
                        </div>
                      </div>
                    )}
                    {step.warning && (
                      <div style={{
                        fontSize: '0.8125rem',
                        color: 'var(--danger)',
                        padding: '8px 12px',
                        background: 'rgba(239, 68, 68, 0.08)',
                        borderRadius: 'var(--radius-sm)',
                        lineHeight: 1.4,
                        marginBottom: 10,
                      }}>
                        ⚠️ {step.warning}
                      </div>
                    )}

                    {/* Action buttons: Challenge + Ask Pocket Mechanic */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => openChallenge(step)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 10px',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border)',
                          background: 'transparent',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {stepPreviouslyChallenged(step.number) ? 'Challenge Again' : "That Doesn't Seem Right"}
                      </button>
                      {onAskPocketMechanic && (
                        <button
                          onClick={() => onAskPocketMechanic(step)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 10px',
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--accent)',
                            background: 'rgba(211, 71, 74, 0.08)',
                            color: 'var(--accent)',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                          </svg>
                          Ask Pocket Mechanic
                        </button>
                      )}
                      {stepChallenges.length > 0 && (
                        <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>
                          {stepChallenges.length} prior {stepChallenges.length === 1 ? 'challenge' : 'challenges'}
                        </span>
                      )}
                    </div>

                    {/* Previous challenge note for this step */}
                    {stepPreviouslyChallenged(step.number) && challengingStep?.number === step.number && (
                      <div style={{
                        marginTop: 8,
                        padding: '8px 12px',
                        fontSize: '0.75rem',
                        color: 'var(--text-muted)',
                        background: 'var(--bg-elevated)',
                        borderRadius: 'var(--radius-sm)',
                        lineHeight: 1.4,
                      }}>
                        You&apos;ve challenged this step before. Previous AI response: &ldquo;{stepChallenges[stepChallenges.length - 1]?.explanation?.slice(0, 100)}...&rdquo;
                        <br />Still have concerns? Provide more details below.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tips & Tricks */}
      {guide.tips_and_tricks?.length > 0 && (
        <Section title="Tips & Tricks">
          {guide.tips_and_tricks.map((tip, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 0' }}>
              <span style={{ color: 'var(--success)', flexShrink: 0 }}>💡</span>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{tip}</span>
            </div>
          ))}
        </Section>
      )}

      {/* Common Mistakes */}
      {guide.common_mistakes?.length > 0 && (
        <Section title="Common Mistakes to Avoid">
          {guide.common_mistakes.map((mistake, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 0' }}>
              <span style={{ color: 'var(--danger)', flexShrink: 0 }}>❌</span>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{mistake}</span>
            </div>
          ))}
        </Section>
      )}

      {/* Challenge History */}
      {challenges.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => setShowChallengeHistory(!showChallengeHistory)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              padding: '12px 16px',
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, flex: 1, textAlign: 'left' }}>
              Step Corrections: {challenges.filter((c) => c.applied).length}
            </span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              style={{ transition: 'transform 0.2s', transform: showChallengeHistory ? 'rotate(180deg)' : 'rotate(0)' }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {showChallengeHistory && (
            <div className="card" style={{ marginTop: 6, padding: '12px 16px' }}>
              {challenges.map((ch, i) => (
                <div key={ch.id || i} style={{
                  padding: '10px 0',
                  borderBottom: i < challenges.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: ch.applied ? '#3b82f6' : 'var(--text-muted)',
                    }} />
                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      Step {ch.stepNumber}: {ch.applied
                        ? (ch.verdict === 'corrected' ? 'Corrected' : 'Clarified')
                        : 'Kept original'}
                    </span>
                    <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                      {ch.date ? new Date(ch.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                    </span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.3, paddingLeft: 14 }}>
                    {ch.explanation?.slice(0, 120)}{(ch.explanation?.length || 0) > 120 ? '...' : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      </div>
      {/* End scrollable content area */}

      {/* Bottom action bar (non-fixed, flex child) */}
      <div style={{
        flexShrink: 0,
        padding: '12px 20px',
        paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
        borderTop: '1px solid var(--border)',
        background: 'var(--bg-primary)',
      }}>
        <button
          className="btn btn-primary"
          onClick={() => {
            deleteGuide(vehicle.id, item.service);
            onMarkComplete?.({
              service: item.service,
              date: new Date().toISOString().split('T')[0],
              type: 'DIY',
              notes: `Completed with DIY Guide (${completedCount}/${totalSteps} steps tracked)`,
              source: 'ai_recommendation',
            });
          }}
          style={{ gap: 10 }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          I Did It — Mark Complete
        </button>

        {/* Save & Finish Later (only when not 100% done) */}
        {progressPct < 100 && (
          <button
            onClick={onBack}
            style={{
              width: '100%',
              padding: '10px 16px',
              marginTop: 8,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              fontWeight: 600,
              fontSize: '0.8125rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            Save &amp; Finish Later
          </button>
        )}

        {/* Delete Repair */}
        <button
          onClick={() => {
            if (window.confirm('Delete this repair guide? Your progress will be lost.')) {
              deleteGuide(vehicle.id, item.service);
              onBack();
            }
          }}
          style={{
            width: '100%',
            padding: '10px 16px',
            marginTop: 8,
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: 'transparent',
            color: 'var(--danger)',
            fontWeight: 600,
            fontSize: '0.8125rem',
            cursor: 'pointer',
          }}
        >
          Delete Repair
        </button>
      </div>

      {/* Challenge Modal (overlay) */}
      {challengingStep && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
        }}>
          <div style={{
            width: '100%',
            maxWidth: 480,
            maxHeight: '85vh',
            overflowY: 'auto',
            background: 'var(--bg-primary)',
            borderRadius: '16px 16px 0 0',
            padding: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Challenge Step {challengingStep.number}</h3>
              <button onClick={cancelChallenge} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Current step preview */}
            <div style={{
              padding: '12px 14px',
              background: 'var(--bg-elevated)',
              borderRadius: 'var(--radius-sm)',
              marginBottom: 16,
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
                Current Step
              </div>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                {challengingStep.title}
              </div>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                {challengingStep.description}
              </p>
            </div>

            {/* Abuse warning */}
            {showAbuseWarning && !challengeResult && (
              <div style={{
                padding: '10px 14px',
                background: 'rgba(234, 179, 8, 0.1)',
                border: '1px solid rgba(234, 179, 8, 0.25)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: 12,
                fontSize: '0.8125rem',
                color: 'var(--text-secondary)',
                lineHeight: 1.4,
              }}>
                Having trouble with this guide? You can also{' '}
                <strong style={{ color: 'var(--text-primary)' }}>start a guided session</strong>{' '}
                to get real-time help, or{' '}
                <strong style={{ color: 'var(--text-primary)' }}>report an issue</strong>{' '}
                if there&apos;s a systematic problem.
              </div>
            )}

            {/* Not yet submitted */}
            {!challengeResult && !challengeLoading && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    marginBottom: 6,
                  }}>
                    What seems wrong?
                  </label>
                  <textarea
                    value={challengeConcern}
                    onChange={(e) => setChallengeConcern(e.target.value)}
                    placeholder='e.g. "I think the bolt size is 17mm, not 19mm for my model year"'
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '0.8125rem',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-elevated)',
                      color: 'var(--text-primary)',
                      resize: 'vertical',
                      lineHeight: 1.5,
                    }}
                  />
                </div>
                <button
                  onClick={submitChallenge}
                  disabled={!challengeConcern.trim()}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    background: challengeConcern.trim() ? 'var(--accent)' : 'var(--bg-elevated)',
                    color: challengeConcern.trim() ? 'white' : 'var(--text-muted)',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    cursor: challengeConcern.trim() ? 'pointer' : 'default',
                    transition: 'all 0.15s',
                  }}
                >
                  Verify This Step
                </button>
              </>
            )}

            {/* Loading */}
            {challengeLoading && (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ ...spinnerContainerSmall }}>
                  <div style={{ ...spinnerStyleSmall }} />
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Verifying step against your vehicle...
                </p>
                <style>{`
                  @keyframes spin { to { transform: rotate(360deg); } }
                `}</style>
              </div>
            )}

            {/* Result */}
            {challengeResult && (
              <div>
                {/* Verdict badge */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 12,
                }}>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: 100,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    background: challengeResult.verdict === 'corrected'
                      ? 'rgba(239, 68, 68, 0.12)'
                      : challengeResult.verdict === 'clarified'
                        ? 'rgba(59, 130, 246, 0.12)'
                        : 'rgba(34, 197, 94, 0.12)',
                    color: challengeResult.verdict === 'corrected'
                      ? 'var(--danger)'
                      : challengeResult.verdict === 'clarified'
                        ? '#3b82f6'
                        : 'var(--success)',
                  }}>
                    {challengeResult.verdict === 'corrected' ? 'Error Found'
                      : challengeResult.verdict === 'clarified' ? 'Clarification Available'
                        : 'Step Confirmed'}
                  </span>
                </div>

                {/* Explanation */}
                <p style={{
                  fontSize: '0.8125rem',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                  marginBottom: 16,
                }}>
                  {challengeResult.explanation}
                </p>

                {/* Corrected step preview */}
                {challengeResult.verdict !== 'confirmed' && (
                  <div style={{
                    padding: '12px 14px',
                    background: 'rgba(59, 130, 246, 0.06)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: 'var(--radius-sm)',
                    marginBottom: 16,
                  }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#3b82f6', textTransform: 'uppercase', marginBottom: 4 }}>
                      Updated Step
                    </div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                      {challengeResult.corrected_step.title}
                    </div>
                    <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                      {challengeResult.corrected_step.description}
                    </p>
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8 }}>
                  {challengeResult.verdict !== 'confirmed' && (
                    <button
                      onClick={applyCorrection}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        borderRadius: 'var(--radius-sm)',
                        border: 'none',
                        background: 'var(--accent)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                      }}
                    >
                      Apply Update
                    </button>
                  )}
                  <button
                    onClick={keepOriginal}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'transparent',
                      color: 'var(--text-secondary)',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                    }}
                  >
                    {challengeResult.verdict === 'confirmed' ? 'Close' : 'Keep Original'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

/* ─── Helper Components ─── */

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h2 style={sectionTitleStyle}>{title}</h2>
      <div className="card" style={{ padding: '12px 16px' }}>
        {children}
      </div>
    </div>
  );
}

function InfoChip({ label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{label}:</span>
      <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}

/* ─── Styles ─── */

const sectionTitleStyle = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 10,
};

const subheadingStyle = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 6,
};

const backBtnStyle = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  background: 'var(--bg-elevated)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  cursor: 'pointer',
  flexShrink: 0,
};

const spinnerContainerStyle = {
  width: 72,
  height: 72,
  margin: '0 auto 24px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const spinnerStyle = {
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  border: '2.5px solid var(--border)',
  borderTopColor: 'var(--accent)',
  animation: 'spin 1s linear infinite',
};

const spinnerContainerSmall = {
  width: 40,
  height: 40,
  margin: '0 auto 12px',
  position: 'relative',
};

const spinnerStyleSmall = {
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  border: '2px solid var(--border)',
  borderTopColor: 'var(--accent)',
  animation: 'spin 1s linear infinite',
};
